import { RPACourse } from '../types';

export const SPECIALIZED_RPA_COURSES: RPACourse[] = [
  {
    id: 'rpa-finance-accounting',
    title: 'RPA for Finance & Accounting',
    description: 'Implement RPA solutions for financial processes including accounts payable, reconciliation, reporting, and compliance.',
    level: 'intermediate',
    duration: '12 hours',
    category: 'specialized',
    platform: 'Multi-Platform',
    certificationPrep: false,
    prerequisites: [],
    learningObjectives: [
      'Automate accounts payable and receivable processes',
      'Implement automated financial reconciliation',
      'Build regulatory reporting automation',
      'Design invoice processing and approval workflows',
      'Create financial data analysis and reporting bots'
    ],
    tags: ['Finance', 'Accounting', 'Invoice Processing', 'Reconciliation', 'Compliance'],
    modules: [
      {
        id: 'finance-automation-fundamentals',
        title: 'Finance Automation Fundamentals',
        description: 'Introduction to RPA applications in finance and accounting',
        content: 'Finance automation opportunities, process identification, and ROI analysis.',
        duration: '1.5 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Identify automation opportunities in finance processes',
          'Calculate ROI for finance automation projects',
          'Map current state finance workflows'
        ]
      },
      {
        id: 'accounts-payable-automation',
        title: 'Accounts Payable Automation',
        description: 'Automate invoice processing, approval workflows, and vendor management',
        content: 'Invoice processing automation, approval routing, vendor master data management.',
        duration: '3 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build invoice data extraction bot',
          'Create approval workflow automation',
          'Implement vendor master data updates'
        ]
      },
      {
        id: 'reconciliation-automation',
        title: 'Financial Reconciliation Automation',
        description: 'Automate bank reconciliation, intercompany matching, and variance analysis',
        content: 'Reconciliation processes, matching algorithms, exception handling, and variance reporting.',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build bank reconciliation bot',
          'Create intercompany matching automation',
          'Implement variance analysis reporting'
        ]
      },
      {
        id: 'reporting-compliance',
        title: 'Financial Reporting and Compliance Automation',
        description: 'Automate regulatory reporting, financial statements, and compliance processes',
        content: 'Regulatory reporting automation, financial consolidation, and compliance monitoring.',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Automate regulatory report generation',
          'Build financial consolidation workflows',
          'Create compliance monitoring dashboards'
        ]
      },
      {
        id: 'accounts-receivable',
        title: 'Accounts Receivable and Collections Automation',
        description: 'Automate customer invoicing, payment processing, and collections',
        content: 'AR automation, payment processing, collections workflows, and customer communication.',
        duration: '2 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build automated invoicing process',
          'Create payment allocation bot',
          'Implement collections workflow automation'
        ]
      },
      {
        id: 'finance-analytics',
        title: 'Financial Analytics and KPI Automation',
        description: 'Automate financial analysis, KPI calculation, and dashboard updates',
        content: 'Financial analytics automation, KPI calculation, and automated dashboard updates.',
        duration: '0.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build financial KPI calculation bot',
          'Create automated dashboard updates',
          'Implement variance analysis automation'
        ]
      }
    ]
  },
  {
    id: 'rpa-hr-customer-service',
    title: 'RPA for HR & Customer Service',
    description: 'Implement RPA solutions for human resources processes and customer service operations to improve efficiency and employee experience.',
    level: 'intermediate',
    duration: '11 hours',
    category: 'specialized',
    platform: 'Multi-Platform',
    certificationPrep: false,
    prerequisites: [],
    learningObjectives: [
      'Automate employee onboarding and offboarding processes',
      'Implement HR data management and reporting automation',
      'Build customer service ticket routing and response automation',
      'Create payroll processing and benefits administration bots',
      'Design employee self-service automation solutions'
    ],
    tags: ['Human Resources', 'Customer Service', 'Onboarding', 'Payroll', 'Ticketing'],
    modules: [
      {
        id: 'hr-automation-overview',
        title: 'HR Automation Opportunities and Strategy',
        description: 'Identify and prioritize HR automation opportunities',
        content: 'HR process analysis, automation potential assessment, and implementation strategy.',
        duration: '1.5 hours',
        difficulty: 'beginner',
        practiceExercises: [
          'Map HR processes for automation potential',
          'Calculate HR automation ROI',
          'Design HR automation roadmap'
        ]
      },
      {
        id: 'employee-lifecycle',
        title: 'Employee Onboarding and Offboarding Automation',
        description: 'Automate complete employee lifecycle processes',
        content: 'Onboarding workflows, system provisioning, offboarding procedures, and access management.',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build employee onboarding automation',
          'Create system access provisioning bot',
          'Implement offboarding checklist automation'
        ]
      },
      {
        id: 'payroll-benefits',
        title: 'Payroll and Benefits Administration Automation',
        description: 'Automate payroll processing, benefits enrollment, and time tracking',
        content: 'Payroll processing automation, benefits administration, and time and attendance tracking.',
        duration: '2 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build payroll calculation automation',
          'Create benefits enrollment workflow',
          'Implement time tracking reconciliation'
        ]
      },
      {
        id: 'hr-reporting',
        title: 'HR Data Management and Reporting',
        description: 'Automate HR analytics, compliance reporting, and data management',
        content: 'HR data consolidation, analytics automation, and compliance reporting.',
        duration: '2 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build HR dashboard automation',
          'Create compliance reporting bot',
          'Implement employee data synchronization'
        ]
      },
      {
        id: 'customer-service-automation',
        title: 'Customer Service Process Automation',
        description: 'Automate ticket routing, response generation, and case management',
        content: 'Ticket automation, response templates, escalation workflows, and customer communication.',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build ticket routing automation',
          'Create automated response system',
          'Implement escalation workflow'
        ]
      },
      {
        id: 'self-service-automation',
        title: 'Employee and Customer Self-Service Automation',
        description: 'Create self-service portals and automated assistance systems',
        content: 'Self-service portal automation, chatbot integration, and automated assistance workflows.',
        duration: '0.5 hours',
        difficulty: 'intermediate',
        practiceExercises: [
          'Build employee self-service automation',
          'Create customer portal integration',
          'Implement automated FAQ responses'
        ]
      }
    ]
  },
  {
    id: 'rpa-security-compliance',
    title: 'RPA Security & Compliance',
    description: 'Master security best practices, compliance frameworks, and risk management for enterprise RPA implementations.',
    level: 'advanced',
    duration: '10 hours',
    category: 'specialized',
    platform: 'Platform Agnostic',
    certificationPrep: false,
    prerequisites: [],
    learningObjectives: [
      'Implement comprehensive RPA security frameworks',
      'Establish compliance monitoring and reporting',
      'Design secure credential and data management',
      'Create audit trails and governance controls',
      'Manage risk assessment and mitigation strategies'
    ],
    tags: ['Security', 'Compliance', 'Risk Management', 'Governance', 'Audit'],
    modules: [
      {
        id: 'rpa-security-framework',
        title: 'RPA Security Framework Development',
        description: 'Establish comprehensive security frameworks for RPA implementations',
        content: 'Security architecture, threat modeling, security controls, and framework development.',
        duration: '2.5 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Conduct RPA security assessment',
          'Design security architecture',
          'Implement security controls framework'
        ]
      },
      {
        id: 'credential-data-security',
        title: 'Secure Credential and Data Management',
        description: 'Implement secure credential storage, encryption, and data protection',
        content: 'Credential management, encryption strategies, data protection, and secret management.',
        duration: '2 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Implement secure credential vault',
          'Design data encryption strategy',
          'Create secure data handling procedures'
        ]
      },
      {
        id: 'compliance-monitoring',
        title: 'Compliance Monitoring and Reporting',
        description: 'Establish compliance frameworks and automated monitoring systems',
        content: 'Compliance frameworks, monitoring systems, reporting automation, and regulatory alignment.',
        duration: '2 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Build compliance monitoring dashboard',
          'Create automated compliance reports',
          'Implement regulatory change tracking'
        ]
      },
      {
        id: 'audit-governance',
        title: 'Audit Trails and Governance Controls',
        description: 'Implement comprehensive audit logging and governance mechanisms',
        content: 'Audit trail design, logging strategies, governance controls, and evidence management.',
        duration: '2 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Design comprehensive audit logging',
          'Create governance control framework',
          'Implement evidence collection automation'
        ]
      },
      {
        id: 'risk-management',
        title: 'Risk Assessment and Mitigation',
        description: 'Develop risk management frameworks and mitigation strategies',
        content: 'Risk assessment methodologies, mitigation strategies, and continuous risk monitoring.',
        duration: '1.5 hours',
        difficulty: 'advanced',
        practiceExercises: [
          'Conduct comprehensive risk assessment',
          'Design risk mitigation strategies',
          'Implement continuous risk monitoring'
        ]
      }
    ]
  }
];